import React, { useEffect, useState, Component } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { RiRefreshLine } from 'react-icons/ri';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import { FaRegEye } from 'react-icons/fa';
import { HiPencil } from 'react-icons/hi2';
import { IoAddSharp } from 'react-icons/io5';
import moment from 'moment/moment';
import LeaseApiService from '../../services/apiService';
import Pagination from '../pagination/pagination';
import SaleVoucherForm from './saleVoucherForm';

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const PAGE_SIZE = 20;

// The stored object's own `name` is a human-readable description ("From 01 Jan,
// 2026 To 31 Dec, 2026"), not the short code the backend's FinancialYear column
// expects - leaseRevenue.js's already-working posting flow sends `fy.id`
// instead, so that's read first here too rather than reconstructing a string
// ourselves (which only re-guesses a format real data already has).
const financialYearCode = (fy) => {
  if (fy?.id) return String(fy.id);
  const startDate = fy?.yearFrom || fy?.YearFrom || fy?.fromDate;
  const year = startDate ? new Date(startDate).getFullYear() : NaN;
  return Number.isNaN(year) ? null : `FY ${year}`;
};
const resolveActiveFinancialYear = () => {
  const savedFY = JSON.parse(localStorage.getItem('selectedFinancialYear') || 'null');
  const savedCode = financialYearCode(savedFY);
  if (savedCode) return savedCode;
  const financialYears = JSON.parse(localStorage.getItem('financialYear') || '[]') || [];
  const active = financialYears?.find((fy) => fy.isActive);
  return financialYearCode(active) || `FY ${new Date().getFullYear()}`;
};

class BtnCellRenderer extends Component {
  render() {
    const { data, onView, onEdit } = this.props;
    const stop = (fn) => (e) => {
      e.stopPropagation();
      fn?.(data);
    };
    return (
      <div className="d-flex gap-3">
        <FaRegEye color="#6E5199" size={16} title="View" style={{ cursor: 'pointer' }} onClick={stop(onView)} />
        <HiPencil
          color={data?.approved ? '#c9c9c9' : '#313a46'}
          size={16}
          title={data?.approved ? 'Approved vouchers cannot be edited' : 'Edit'}
          style={{ cursor: data?.approved ? 'not-allowed' : 'pointer' }}
          onClick={data?.approved ? undefined : stop(onEdit)}
        />
      </div>
    );
  }
}

const SaleVoucherListing = () => {
  const apiService = new LeaseApiService();
  const activeFY = resolveActiveFinancialYear();

  const [loading, setLoading] = useState(true);
  const [rowData, setRowData] = useState([]);
  const [dropdowns, setDropdowns] = useState(null);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0 });
  const [FilterTab, setFilterTab] = useState(true);
  const [formModal, setFormModal] = useState({ mode: null, voucherNo: null });

  const [filters, setFilters] = useState({
    voucherNo: { value: '', label: 'All' },
    fromDate: moment().startOf('year').toDate(),
    toDate: new Date(),
  });

  useEffect(() => {
    fetchDropdowns();
    getItems(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDropdowns = async () => {
    try {
      const params = new URLSearchParams();
      params.append('financialYear', activeFY);
      const res = await apiService.getApiParamater('SALE_VOUCHER_DROPDOWNS', `?${params.toString()}`);
      if (res?.data?.status) {
        setDropdowns(res.data.data);
      }
    } catch (error) {
      console.error('Error fetching sale voucher dropdowns:', error);
    }
  };

  const getItems = async (pageNo = 1) => {
    try {
      setLoading(true);
      const body = {
        take: PAGE_SIZE,
        skip: (pageNo - 1) * PAGE_SIZE,
        sortColumn: 'id',
        sortDirection: 1,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        financialYear: activeFY,
        voucherNo: filters.voucherNo?.value || '',
      };
      const res = await apiService.post('SALE_VOUCHER_LISTING', body);
      if (res?.data?.status) {
        const items = res?.data?.data?.items || [];
        const count = res?.data?.data?.count || 0;
        setRowData(items);
        setPagination({ currentPage: pageNo, totalPages: Math.max(Math.ceil(count / PAGE_SIZE), 1), totalRecords: count });
        if (!items.length) toast.info('No data found');
      } else {
        toast.error(res?.data?.data || 'Something went wrong');
      }
    } catch (error) {
      console.error('Error fetching sale vouchers:', error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({ voucherNo: { value: '', label: 'All' }, fromDate: moment().startOf('year').toDate(), toDate: new Date() });
    getItems(1);
  };

  const dateFormatter = (params) => (params.value ? moment(params.value).format('DD-MMM-YYYY') : '');
  const numberFormatter = (params) => (params.value == null ? '' : Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

  const columnDefs = [
    {
      headerName: 'Actions',
      cellRenderer: BtnCellRenderer,
      pinned: 'left',
      cellRendererParams: {
        onView: (row) => setFormModal({ mode: 'view', voucherNo: row.voucherNo }),
        onEdit: (row) => setFormModal({ mode: 'edit', voucherNo: row.voucherNo }),
      },
      suppressMovable: true,
      sortable: false,
      filter: false,
      width: 100,
    },
    { headerName: 'Voucher No', field: 'voucherNo', filter: true, floatingFilter: true, sortable: true, unSortIcon: true, suppressMovable: true, width: 150, pinned: 'left' },
    { headerName: 'Approved', field: 'isApproved', filter: true, floatingFilter: true, sortable: true, unSortIcon: true, suppressMovable: true, width: 110, valueFormatter: (p) => (p.value ? 'Yes' : 'No') },
    { headerName: 'Posting Type', field: 'postingType', filter: true, floatingFilter: true, sortable: true, unSortIcon: true, suppressMovable: true, width: 130 },
    { headerName: 'Posting Date', field: 'postingDate', filter: true, floatingFilter: true, sortable: true, unSortIcon: true, suppressMovable: true, width: 140, valueFormatter: dateFormatter },
    { headerName: 'Ref No', field: 'refNo', filter: true, floatingFilter: true, sortable: true, unSortIcon: true, suppressMovable: true, width: 130 },
    { headerName: 'Amount Credit', field: 'amountCredit', filter: true, floatingFilter: true, sortable: true, unSortIcon: true, suppressMovable: true, width: 150, valueFormatter: numberFormatter },
    { headerName: 'Amount Debit', field: 'amountDebit', filter: true, floatingFilter: true, sortable: true, unSortIcon: true, suppressMovable: true, width: 150, valueFormatter: numberFormatter },
    { headerName: 'Transaction Method', field: 'transactionMethod', filter: true, floatingFilter: true, sortable: true, unSortIcon: true, suppressMovable: true, width: 170 },
    { headerName: 'Transaction Date', field: 'transactionDate', filter: true, floatingFilter: true, sortable: true, unSortIcon: true, suppressMovable: true, width: 150, valueFormatter: dateFormatter },
    { headerName: 'Created By', field: 'createdBy', filter: true, floatingFilter: true, sortable: true, unSortIcon: true, suppressMovable: true, width: 150 },
    { headerName: 'Created On', field: 'createdOn', filter: true, floatingFilter: true, sortable: true, unSortIcon: true, suppressMovable: true, width: 150, valueFormatter: dateFormatter },
    { headerName: 'Cost Center 1', field: 'costCenter1', filter: true, floatingFilter: true, sortable: true, unSortIcon: true, suppressMovable: true, width: 140 },
    { headerName: 'Cost Center 2', field: 'costCenter2', filter: true, floatingFilter: true, sortable: true, unSortIcon: true, suppressMovable: true, width: 140 },
    { headerName: 'Cost Center 3', field: 'costCenter3', filter: true, floatingFilter: true, sortable: true, unSortIcon: true, suppressMovable: true, width: 140 },
    { headerName: 'Cost Center 4', field: 'costCenter4', filter: true, floatingFilter: true, sortable: true, unSortIcon: true, suppressMovable: true, width: 140 },
  ];

  const voucherOptions = [{ value: '', label: 'All' }, ...(dropdowns?.vouchers?.map((v) => ({ value: v?.voucherNo, label: v?.voucherNo })) || [])];

  const closeFormModal = () => setFormModal({ mode: null, voucherNo: null });
  const handleFormSaved = () => {
    closeFormModal();
    getItems(1);
  };

  return (
    <div className="p-3">
      <div className="cost-center-section">
        <div className="d-flex mt-3 cursor-pointer slide-header" onClick={() => setFilterTab(!FilterTab)}>
          <div className="h6 pt-1">Search Parameters</div>
        </div>
        <div className={`slide-content ${FilterTab ? 'open' : 'closed'}`}>
          <div className={`border-box2 p-3 ${FilterTab ? 'slide-in' : 'slide-out'}`}>
            <div className="form-group grid-contain">
              <div className="d-flex">
                <label className="form-label pt-1 start">Voucher No</label>
                <Select
                  className="p-0 w-100"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  options={voucherOptions}
                  value={filters.voucherNo}
                  onChange={(o) => setFilters((prev) => ({ ...prev, voucherNo: o }))}
                />
              </div>
              <div className="d-flex">
                <label className="form-label pt-1 start">From Date</label>
                <DatePicker selected={filters.fromDate} onChange={(date) => setFilters((prev) => ({ ...prev, fromDate: date }))} dateFormat="dd-MMM-yyyy" className="p-2 form-control w-100" />
              </div>
              <div className="d-flex">
                <label className="form-label pt-1 start">To Date</label>
                <DatePicker selected={filters.toDate} onChange={(date) => setFilters((prev) => ({ ...prev, toDate: date }))} dateFormat="dd-MMM-yyyy" className="p-2 form-control w-100" />
              </div>
              <div className="d-flex gap-1">
                <div onClick={() => getItems(1)} className="mainbtn flex-end gap-2 w-50">
                  <FaFileCircleExclamation color="#fff" className="center mt-2 mx-2" />
                  <div className="center">Search</div>
                </div>
                <div onClick={handleClearFilters} className="mainbtnRed flex-end gap-2 w-50">
                  <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                  <div className="center">Reset</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-box2 p-3 mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mainInvHead mb-0">Sale Voucher</h5>
          <button className="mainbtn" type="button" onClick={() => setFormModal({ mode: 'create', voucherNo: null })}>
            <IoAddSharp color="#fff" className="center mt-1" /> <span className="ms-1">Add</span>
          </button>
        </div>
        <div className="gridshad">
          <div className="ag-theme-quartz" style={{ height: 650 }}>
            <AgGridReact
              getRowStyle={(params) => (params.node.rowIndex % 2 === 0 ? { background: '#F0EDF5' } : { background: '#fff' })}
              rowData={rowData}
              loading={loading}
              enableFilter={true}
              floatingFilter={true}
              columnDefs={columnDefs}
            />
          </div>
          <Pagination pagination={pagination} getItems={getItems} />
        </div>
      </div>

      <Modal show={!!formModal.mode} onHide={closeFormModal} backdrop="static" keyboard={false} centered dialogClassName="sale-voucher-form-modal" contentClassName="p-0" size="xl">
        <Modal.Body style={{ padding: 0 }}>
          {formModal.mode && (
            <SaleVoucherForm mode={formModal.mode} voucherNo={formModal.voucherNo} dropdowns={dropdowns} onClose={closeFormModal} onSaved={handleFormSaved} />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SaleVoucherListing;
